import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { useActionableCommentStore } from '@/features/actionable-comment'
import { useMarkThreadActionable } from '@/features/annotation-management'
import { ThreadActionableType } from '@/schema'

export const NotActionableDialog: React.FC = () => {
  const { t } = useTranslation()
  const { notActionableDialogOpen, threadId, setNotActionableDialogOpen } =
    useActionableCommentStore()
  const { mutate: markThreadActionable, isPending } = useMarkThreadActionable()

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setNotActionableDialogOpen(newOpen)
    },
    [setNotActionableDialogOpen]
  )

  const handleConfirm = useCallback(() => {
    markThreadActionable(
      {
        id: threadId || '',
        data: { actionable_type: ThreadActionableType.NOT_ACTIONABLE },
      },
      {
        onSuccess: () => {
          triggerToast(
            'success',
            t('tflViewer.notActionableDialog.messages.success')
          )
          setNotActionableDialogOpen(false)
        },
        onError: () => {
          triggerToast(
            'error',
            t('tflViewer.notActionableDialog.messages.error')
          )
        },
      }
    )
  }, [markThreadActionable, setNotActionableDialogOpen, threadId, t])

  return (
    <Dialog open={notActionableDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="!overflow-hidden flex flex-col max-h-[95dvh]">
          <DialogHeader className="space-y-0.5 flex-shrink-0">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {t('tflViewer.notActionableDialog.title')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t('tflViewer.notActionableDialog.description')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3 flex-shrink-0">
            <Button
              type="button"
              variant="default"
              onClick={() => handleOpenChange(false)}
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white text-[--Colors-Gray-light-700]"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent text-white bg-brand-solid hover:bg-brand-solid-hover"
              variant="default"
              disabled={isPending}
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
